// Voice editor — analyse-from-history review modal (US-098 + US-099).
//
// Renders the "Voici ce que j'ai appris de ta voix" diff modal from
// `prototypes_and_docs/ls-voice.jsx:274-386`. For each field where the
// suggestion differs from the current value we render a checkbox row with
// `current → suggested`. The footer counts the accepted rows and applies
// only the checked subset on submit.

import { useMemo, useState } from 'react';

import { useT } from '@barel/i18n';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@barel/ui';

import type { VoiceRow, VoiceSuggestionPatch } from '@barel/api-client/react';

export interface AnalysisSuggestionsModalProps {
  open: boolean;
  voice: VoiceRow;
  suggestion: VoiceSuggestionPatch;
  /** Apply (only the accepted fields). Resolves once the parent's mutation finishes. */
  onApply: (patch: VoiceSuggestionPatch) => Promise<void>;
  onClose: () => void;
  busy?: boolean;
}

type DiffField =
  | 'tone'
  | 'length'
  | 'humor'
  | 'emoji'
  | 'language'
  | 'formality'
  | 'adaptive'
  | 'banned'
  | 'signature';

const DIFFABLE_FIELDS: DiffField[] = [
  'tone',
  'length',
  'humor',
  'emoji',
  'language',
  'formality',
  'adaptive',
  'banned',
  'signature',
];

interface DiffRow {
  field: DiffField;
  label: string;
  current: string;
  suggested: string;
  rawSuggested: VoiceSuggestionPatch[DiffField];
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
  }
  return false;
}

function format(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (Array.isArray(value)) return value.length === 0 ? '∅' : value.join(', ');
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return String(value);
}

export function AnalysisSuggestionsModal({
  open,
  voice,
  suggestion,
  onApply,
  onClose,
  busy = false,
}: AnalysisSuggestionsModalProps) {
  const t = useT('voice');

  const labelFor = useMemo<Record<DiffField, string>>(
    () => ({
      tone: t('review.fieldTone'),
      length: t('review.fieldLength'),
      humor: t('review.fieldHumor'),
      emoji: t('review.fieldEmoji'),
      language: t('review.fieldLanguage'),
      formality: t('review.fieldFormality'),
      adaptive: t('review.fieldAdaptive'),
      banned: t('review.fieldBanned'),
      signature: t('review.fieldSignature'),
    }),
    [t],
  );

  const diffs = useMemo<DiffRow[]>(() => {
    const out: DiffRow[] = [];
    for (const field of DIFFABLE_FIELDS) {
      const proposed = suggestion[field];
      if (proposed === undefined) continue;
      const current = voice[field];
      if (isEqual(current, proposed)) continue;
      out.push({
        field,
        label: labelFor[field],
        current: format(current),
        suggested: format(proposed),
        rawSuggested: proposed,
      });
    }
    return out;
  }, [suggestion, voice, labelFor]);

  const [accepted, setAccepted] = useState<Record<string, boolean>>(() =>
    diffs.reduce<Record<string, boolean>>((acc, d) => {
      acc[d.field] = true;
      return acc;
    }, {}),
  );

  // Re-prime the accepted set when the suggestion changes.
  useMemo(() => {
    setAccepted(
      diffs.reduce<Record<string, boolean>>((acc, d) => {
        acc[d.field] = true;
        return acc;
      }, {}),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion.convsAnalysed, suggestion.notes, suggestion.span, diffs.length]);

  const acceptedCount = diffs.filter((d) => accepted[d.field]).length;
  const total = diffs.length;
  const span = suggestion.span ?? 'none';
  const empty = total === 0;

  const handleApply = async () => {
    if (empty) {
      onClose();
      return;
    }
    const patch: VoiceSuggestionPatch = {};
    for (const d of diffs) {
      if (accepted[d.field]) {
        // Type narrowing — assigning per-field keeps the union sound.
        (patch as Record<string, unknown>)[d.field] = d.rawSuggested;
      }
    }
    await onApply(patch);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent size="lg" data-testid="voice-analysis-suggestions">
        <DialogHeader
          title={t('review.title')}
          description={t('review.subtitle', {
            count: suggestion.convsAnalysed ?? 0,
            span,
          })}
          onClose={onClose}
        />
        <DialogBody>
          {empty ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <div className="text-sm font-semibold text-primary">{t('review.emptyTitle')}</div>
              <p className="text-xs text-secondary">{t('review.emptyBody')}</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3" data-testid="voice-analysis-rows">
              {diffs.map((d) => (
                <li
                  key={d.field}
                  className="flex items-start gap-3 rounded-lg border border-default bg-bg-elevated px-3 py-2"
                  data-testid={`voice-analysis-row-${d.field}`}
                >
                  <Checkbox
                    checked={accepted[d.field] ?? true}
                    onCheckedChange={(value: boolean | 'indeterminate') => {
                      setAccepted((prev) => ({ ...prev, [d.field]: value === true }));
                    }}
                    aria-label={d.label}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-primary">{d.label}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-secondary">
                      <span className="line-through text-tertiary">
                        {t('review.rowFromLabel')}: {d.current}
                      </span>
                      <span aria-hidden>→</span>
                      <span className="font-semibold text-accent-default">
                        {t('review.rowToLabel')}: {d.suggested}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          {!empty ? (
            <span className="mr-auto text-xs text-tertiary" data-testid="voice-analysis-count">
              {t('review.selected', { accepted: acceptedCount, total })}
            </span>
          ) : null}
          <Button variant="ghost" onClick={onClose} type="button" disabled={busy}>
            {t('review.cancelCta')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleApply();
            }}
            disabled={!empty && acceptedCount === 0}
            loading={busy}
            data-testid="voice-analysis-apply"
            type="button"
          >
            {empty ? t('review.cancelCta') : t('review.applyCta', { count: acceptedCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
